// useScraperSubscriptions.ts - React Query hooks for scraper subscriptions
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { notifications } from '@mantine/notifications';

export interface ScraperSubscription {
  id: string;
  companyId: string;
  source: string;
  enabled: boolean;
  subscribedAt: number;
  createdAt: number;
  updatedAt: number;
}

export interface AvailableScraper {
  source: string;
  name: string;
  description?: string;
  available: boolean;
  subscribed?: boolean;
  enabled?: boolean;
  subscriptionId?: string;
}

/**
 * Get all available scrapers with subscription status
 */
export function useAvailableScrapers() {
  return useQuery<AvailableScraper[]>({
    queryKey: ['scrapers', 'available'],
    queryFn: async () => {
      const response = await api.scrapers.getAvailable();
      return response.data || [];
    },
  });
}

/**
 * Get company's scraper subscriptions
 */
export function useScraperSubscriptions() {
  return useQuery<ScraperSubscription[]>({
    queryKey: ['scrapers', 'subscriptions'],
    queryFn: async () => {
      const response = await api.scrapers.getSubscriptions();
      return response.data || [];
    },
  });
}

/**
 * Mutation to subscribe to a scraper
 */
export function useSubscribeToScraper() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (source: string) => {
      const response = await api.scrapers.subscribe(source);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrapers'] });
      queryClient.invalidateQueries({ queryKey: ['scrapers', 'available'] });
      queryClient.invalidateQueries({ queryKey: ['scrapers', 'subscriptions'] });
      notifications.show({
        title: 'Subscribed',
        message: 'Successfully subscribed to scraper',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to subscribe',
        color: 'red',
      });
    },
  });
}

/**
 * Mutation to unsubscribe from a scraper
 */
export function useUnsubscribeFromScraper() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (source: string) => {
      const response = await api.scrapers.unsubscribe(source);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrapers'] });
      queryClient.invalidateQueries({ queryKey: ['scrapers', 'available'] });
      queryClient.invalidateQueries({ queryKey: ['scrapers', 'subscriptions'] });
      notifications.show({
        title: 'Unsubscribed',
        message: 'Successfully unsubscribed from scraper',
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to unsubscribe',
        color: 'red',
      });
    },
  });
}

/**
 * Mutation to enable/disable a subscription
 */
export function useToggleSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ source, enabled }: { source: string; enabled: boolean }) => {
      const response = await api.scrapers.toggleSubscription(source, enabled);
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['scrapers'] });
      queryClient.invalidateQueries({ queryKey: ['scrapers', 'subscriptions'] });
      notifications.show({
        title: 'Updated',
        message: `Subscription ${variables.enabled ? 'enabled' : 'disabled'}`,
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update subscription',
        color: 'red',
      });
    },
  });
}
